// components/charts/leads/FormKeyStatsChart.tsx
import React, { useMemo, useState, useEffect, useCallback } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
} from "recharts";
import { Input } from "antd";

import { useSelector } from "react-redux";
import {
  selectSelectedCompany,
  selectGlobalFilters,
} from "../../../../../../core/data/redux/dashboardSlice";

import CommonSelect from "../../../../../../core/common/commonSelect";
import api from "../../../../../../services/api";
import "../../../styles/charts/FormKeyStatsChart.scss";

const { Search } = Input;

// Cache interface matching dashboard slice pattern
interface CachedData {
  data: any;
  lastUpdated: number;
  filtersHash: string;
}

// In-memory cache object
const chartCache: Record<string, CachedData> = {};
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const FormKeyStatsChart: React.FC<any> = ({ type }) => {
  const selectedCompany = useSelector(selectSelectedCompany);
  const globalFilters = useSelector(selectGlobalFilters);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<any>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);

  // Search
  const [searchTerm, setSearchTerm] = useState("");

  // Local filters specific to this chart
  const [groupBy, setGroupBy] = useState("state");

  const fetchData = useCallback(async (forceRefresh = false) => {
    if (!selectedCompany?.company_id) return;

    const signature = localStorage.getItem("signature");
    const requestBody: any = {
      signature,
      company_id: parseInt(selectedCompany.company_id),
      company_type: selectedCompany.company_type,
      type: type || 1,
      filters: {
        ...globalFilters,
        group_by: groupBy,
      },
    };

    // Generate filters hash (same pattern as dashboard slice)
    const currentFiltersHash = JSON.stringify({
      company_id: requestBody.company_id,
      company_type: requestBody.company_type,
      type: requestBody.type,
      filters: requestBody.filters,
    });

    // Create cache key based on component identifier
    const cacheKey = `formKeyStats_${type}`;

    // Check if cache is valid
    const cachedData = chartCache[cacheKey];
    const isCacheValid =
      cachedData?.data &&
      !forceRefresh &&
      Date.now() - (cachedData.lastUpdated || 0) < CACHE_DURATION &&
      cachedData.filtersHash === currentFiltersHash;

    // Return cached data if valid
    if (isCacheValid) {
      setData(cachedData.data);
      setCurrentPage(1);
      setSearchTerm("");
      return;
    }

    // Fetch from API
    setLoading(true);
    setError(null);

    try {
      const response = await api.post(
        "leadService/getLeadStatsByFormKey",
        requestBody
      );

      const responseData = response?.data?.data;

      // Store in cache with timestamp and filters hash
      chartCache[cacheKey] = {
        data: responseData,
        lastUpdated: Date.now(),
        filtersHash: currentFiltersHash,
      };

      setData(responseData);
      setCurrentPage(1);
      setSearchTerm("");
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [selectedCompany, globalFilters, groupBy, type]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const groupByOptions = [
    { value: "all", label: "All" },
    { value: "state", label: "By State" },
    { value: "city", label: "By City" },
    { value: "country", label: "By Country" },
    { value: "district", label: "By District" },
  ];

  // Filter data based on search term
  const filteredChartData = useMemo(() => {
    if (!data?.leads?.form_key_stats) return [];

    const allData = data.leads.form_key_stats;

    if (!searchTerm.trim()) return allData;

    const searchLower = searchTerm.toLowerCase();
    return allData.filter((item: any) =>
      item.name.toLowerCase().includes(searchLower)
    );
  }, [data, searchTerm]);

  // Pagination calculations on filtered data
  const totalPages = Math.ceil(filteredChartData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentPageData = filteredChartData.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  const hasData = filteredChartData.length > 0;

  const handlePrevPage = () => setCurrentPage((prev) => Math.max(prev - 1, 1));
  const handleNextPage = () =>
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleRefresh = () => {
    fetchData(true); // Force refresh bypasses cache
  };

  const CustomXAxisTick = (props: any) => {
    const { x, y, payload } = props;
    const text = payload.value;
    const maxLength = 15;
    const maxLines = 3;
    const lines = [];

    if (text.length > maxLength) {
      const words = text.split(" ");

      if (words.length > 1) {
        let currentLine = "";

        for (const word of words) {
          const testLine = currentLine ? `${currentLine} ${word}` : word;

          if (testLine.length <= maxLength) {
            currentLine = testLine;
          } else {
            if (currentLine) {
              lines.push(currentLine);
              if (lines.length >= maxLines - 1) {
                const remainingWords = words.slice(words.indexOf(word));
                const remainingText = remainingWords.join(" ");
                if (remainingText.length > maxLength) {
                  lines.push(remainingText.substring(0, maxLength - 3) + "...");
                } else {
                  lines.push(remainingText);
                }
                break;
              }
              currentLine = word;
            } else {
              if (word.length > maxLength) {
                lines.push(word.substring(0, maxLength - 3) + "...");
                break;
              } else {
                currentLine = word;
              }
            }
          }
        }

        if (currentLine && lines.length < maxLines) {
          lines.push(currentLine);
        }
      } else {
        const word = text;
        if (word.length > maxLength * maxLines) {
          lines.push(word.substring(0, maxLength - 3) + "...");
        } else if (word.length > maxLength * 2) {
          const partLength = Math.ceil(word.length / 3);
          lines.push(word.substring(0, partLength));
          lines.push(word.substring(partLength, partLength * 2));
          lines.push(word.substring(partLength * 2));
        } else if (word.length > maxLength) {
          const mid = Math.ceil(word.length / 2);
          lines.push(word.substring(0, mid));
          lines.push(word.substring(mid));
        } else {
          lines.push(word);
        }
      }
    } else {
      lines.push(text);
    }

    return (
      <g transform={`translate(${x},${y})`}>
        {lines.map((line, i) => (
          <text
            key={i}
            x={0}
            y={i * 14}
            fill="#6b7280"
            textAnchor="middle"
            fontSize={11}
            dominantBaseline="hanging">
            {line}
          </text>
        ))}
      </g>
    );
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]?.payload;

      return (
        <div className="form-key-tooltip">
          <p className="tooltip-title">{label}</p>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot total-leads" />
              <span>Total</span>
            </div>
            <span className="tooltip-value">{data?.total_leads || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot converted" />
              <span>Converted</span>
            </div>
            <span className="tooltip-value">{data?.converted || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot in-process" />
              <span>In Process</span>
            </div>
            <span className="tooltip-value">{data?.in_process || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot lost" />
              <span>Lost</span>
            </div>
            <span className="tooltip-value">{data?.lost || 0}</span>
          </div>
          <div className="tooltip-item">
            <div className="tooltip-label">
              <div className="tooltip-dot conversion-rate" />
              <span>Conversion Rate</span>
            </div>
            <span className="tooltip-value">
              {data?.total_leads > 0
                ? ((data.converted / data.total_leads) * 100).toFixed(1)
                : 0}
              %
            </span>
          </div>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="form-key-chart-container">
        <div className="chart-loading-state">
          <div className="loading-spinner">
            <i className="ti ti-loader-2 spin" />
          </div>
          <p>Loading chart data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="form-key-chart-container">
        <div className="chart-error-state">
          <i className="ti ti-alert-circle text-danger" />
          <p className="text-danger">Error loading data!</p>
          <small className="text-muted">{error}</small>
        </div>
      </div>
    );
  }

  return (
    <div className="form-key-chart-container m-0">
      <div className="chart-header-controls">
        <div className="d-flex gap-3 align-items-center justify-content-between mb-2">
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <div style={{ width: "120px" }}>
              <CommonSelect
                className="select small"
                options={groupByOptions}
                defaultValue={groupByOptions.find(
                  (opt) => opt.value === groupBy
                )}
                onChange={(selected: any) => setGroupBy(selected.value)}
                placeHolder="Group By"
              />
            </div>

            <Search
              placeholder={`Search ${groupBy === "all" ? "items" : groupBy}...`}
              allowClear
              value={searchTerm}
              onChange={handleSearchChange}
              onSearch={handleSearch}
              style={{ width: 200 }}
              size="middle"
              className="antd-chart-search p-2"
            />

            {searchTerm && (
              <div className="search-results-info">
                <small className="text-muted">
                  <i className="ti ti-filter" /> Found{" "}
                  {filteredChartData.length} result
                  {filteredChartData.length !== 1 ? "s" : ""} for "{searchTerm}"
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="chart-wrapper">
        {hasData ? (
          <>
            <ResponsiveContainer width="100%" height="90%">
              <ComposedChart
                data={currentPageData}
                margin={{ top: 25, right: 20, left: 40, bottom: -50 }}>
                <CartesianGrid
                  strokeDasharray="2 4"
                  stroke="#d1d5db"
                  horizontal={true}
                  vertical={true}
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={true}
                  tick={<CustomXAxisTick />}
                  height={90}
                  interval={0}
                />
                <YAxis
                  axisLine={false}
                  tickLine={true}
                  tick={{ fontSize: 13, fill: "#6b7280" }}
                  width={30}
                />
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{
                    stroke: "#d1d5db",
                    strokeWidth: 1,
                    strokeDasharray: "4 4",
                  }}
                />

                <Area
                  type="monotone"
                  dataKey="in_process"
                  fill="rgba(245, 158, 11, 0.1)"
                  stroke="none"
                />
                <Area
                  type="monotone"
                  dataKey="converted"
                  fill="rgba(16, 185, 129, 0.1)"
                  stroke="none"
                />

                <Bar
                  dataKey="converted"
                  stackId="a"
                  fill="#10b981"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="in_process"
                  stackId="a"
                  fill="#f59e0b"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="lost"
                  stackId="a"
                  fill="#ef4444"
                  radius={[4, 4, 0, 0]}
                />

                <Line
                  type="monotone"
                  dataKey="total_leads"
                  stroke="#8b5cf6"
                  strokeWidth={2}
                  dot={{ fill: "#8b5cf6", r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </ComposedChart>
            </ResponsiveContainer>

            <div className="chart-controls">
              <div className="pagination-info">
                <span className="text-muted">
                  Showing {startIndex + 1}-
                  {Math.min(
                    startIndex + itemsPerPage,
                    filteredChartData.length
                  )}{" "}
                  of {filteredChartData.length}{" "}
                </span>
              </div>
              <div className="pagination-controls">
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}>
                  <i className="ti ti-chevron-left" />
                  Prev
                </button>
                <button
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}>
                  Next
                  <i className="ti ti-chevron-right" />
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleRefresh}
                  disabled={loading}>
                  <i className="ti ti-refresh" />
                  Refresh
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="no-data-message">
            <i className="ti ti-database-off" />
            <p>
              {searchTerm
                ? `No results found for "${searchTerm}"`
                : "No data available for selected filters!"}
            </p>
            {searchTerm && (
              <button
                className="btn btn-sm btn-outline-primary mt-2"
                onClick={() => setSearchTerm("")}>
                Clear Search
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FormKeyStatsChart;
